import { isEmpty } from 'lodash';
import { type FC } from 'react';

import { useAriaTitle } from '~src/commons/utils/hooks/a11y-hooks';
import { useTranslations } from '~src/commons/utils/translations/translate';

import { DeliveryAddressManagerFSM } from '~address-form/delivery-address-renderer-fsm';
import { isAddressEmpty, isExternalAddressBook } from '~address-form/utils/address-utils';

import { CountrySelectProxy as CountrySelect } from '../country/country-select';
import { Heading } from '../ui/heading';
import { useGlobalState } from './../global-state';
import { AddressFormProxy } from './search-address/address-form';
import { AddressInputInline } from './search-address/address-input-inline';
import { UpfrontAddressButton } from './search-address/upfront-address';

const HiddenAddressPlaceholder: FC = () => {
  /**
   * Dummy component preventing ipc channel pollution
   */
  const { tICU } = useTranslations();

  useAriaTitle(tICU('widget.aria.CHECKOUT_WITHOUT_ADDRESS.TITLE'), 'checkout');

  return null;
};

export const AddressManager: FC = () => {
  const globalState = useGlobalState();

  // TODO: move the config to global state.
  const session = globalState.sessions[0];
  const { flags } = session;
  const { tICU, locale } = useTranslations();

  const isPreAddress = isAddressEmpty(globalState.search_address);

  /*
   * Reset everything when either locale or country changes ie fetch forms,
   * reset autocomplete machine
   */
  const key = `${globalState.purchase_country}_${locale}`;

  if (globalState.use_recipient_address) {
    return (
      <div css={styles.wrapper}>
        {isExternalAddressBook || (
          <div css={styles.headingWrapper}>
            <Heading>{tICU('widget.RECIPIENT_ADDRESS_HEADER')}</Heading>
            <CountrySelect />
          </div>
        )}
        <DeliveryAddressManagerFSM key={key} />
      </div>
    );
  }

  /*
   * 1. Flag enable_address_form=false - disable address input altogether.
   * 2. Flag display_upfront_address=false - disable address input pre-address.
   * (we always show address input post-address, regardless of this flag)
   */
  if (!flags.enable_address_form || (!flags.display_upfront_address && isPreAddress)) {
    return <HiddenAddressPlaceholder />;
  }

  /*
   * Show the "check local delivery options" button.
   * 1. Only pre-address.
   * 2. In conjuction with global delivery options already shown.
   * 3. When the upfront_carriers object is set.
   *
   * note: the upfront_carriers object in configuration is uniquely related to
   * the upfront address features, the display_upfront_address flag is also
   * used to show the general address input pre-address.
   */
  if (
    flags.display_upfront_address &&
    isPreAddress &&
    flags.show_shipping_categories_before_address &&
    !isEmpty(session.upfront_carriers)
  ) {
    return (
      <div css={styles.wrapper}>
        <Heading>{tICU('widget.CUSTOMER_ADDRESS_HEADER')}</Heading>
        <UpfrontAddressButton key={key} />
      </div>
    );
  }

  /*
   * In case of postal code only we show just the postal code input. Anything
   * more than that and we show the regular address modal.
   */
  const hasComplexAddress =
    flags.show_street_on_address_module || flags.show_city_on_address_module || flags.show_region;

  if (hasComplexAddress) {
    return (
      <div css={styles.wrapper}>
        <div css={styles.headingWrapper}>
          <Heading>{tICU('widget.CUSTOMER_ADDRESS_HEADER')}</Heading>
          <CountrySelect />
        </div>
        <AddressFormProxy key={key} />
      </div>
    );
  } else {
    return (
      <div css={styles.wrapper}>
        <div css={styles.headingWrapper}>
          <Heading>{tICU('widget.CUSTOMER_POSTALCODE_HEADER')}</Heading>
          <CountrySelect />
        </div>
        <AddressInputInline key={key} />
      </div>
    );
  }
};

const styles = {
  wrapper: {
    marginBottom: '32px',
  },
  headingWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
};
